import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../common/types';
import { UserAvatar } from '../common/views/userAvatar/UserAvatar';
import { SMLUpgradeFeature } from '../music24/config/music24PlanCapabilities';
import { useNextPlanForFeature } from '../music24/hooks/useNextPlanForFeature';
import { ArtistSwitcherLimitBox } from './ArtistSwitcherLimitBox';
import type { ArtistSwitcherState } from './useArtistSwitcher';
import { artistSwitcherMessages as messages } from './artistSwitcherMessages';
import { isDemoAccount } from './demoAccounts';
import { isReadOnlyAccount } from './readOnlyAccounts';
import { ReadOnlyArtistTag } from './ReadOnlyArtistTag';
import { DemoArtistTag } from './DemoArtistTag';
import styles from './ArtistSwitcherSheetList.module.scss';

interface ArtistSwitcherSheetListProps {
  switcher: ArtistSwitcherState;
  /** Closes the sheet that holds the list. */
  onClose: () => void;
  /** design: workspaces a3 / b1. The rows are dimmed and do not react while a workspace opens. */
  isDisabled?: boolean;
}

/**
 * design: navigation i1. The artist rows of a mobile sheet, "Add artist" and the at-limit box. A
 * tap on an artist switches and closes the sheet. The artist sheet and the account sheet
 * (design: workspaces b1) share it.
 */
const ArtistSwitcherSheetListComponent: React.FC<ArtistSwitcherSheetListProps> = ({
  switcher,
  onClose,
  isDisabled = false,
}) => {
  const intl = useIntl();
  const { accounts, ownAccountCount, activeAccountId, artistsLimit, onSelect, onUpgradeHint } = switcher;
  const { current, nextPlan } = useNextPlanForFeature(SMLUpgradeFeature.artists);
  const atLimit = artistsLimit !== null && (ownAccountCount ?? 0) >= artistsLimit;

  const select = useCallback(
    (accountId: number) => {
      onSelect(accountId);
      onClose();
    },
    [onClose, onSelect]
  );
  const upgrade = useCallback(() => {
    onClose();
    onUpgradeHint();
  }, [onClose, onUpgradeHint]);

  return (
    <>
      <ul className={styles.list}>
        {accounts?.map((account) => {
          const isActive = account.id === activeAccountId;
          return (
            <li key={account.id}>
              <button
                type="button"
                className={styles.row}
                onClick={() => select(account.id)}
                disabled={isDisabled}
                aria-current={isActive ? 'true' : undefined}
              >
                <UserAvatar size={32} url={account.avatarProcessedUrl} name={account.name} />
                <span className={styles.name}>{account.name}</span>
                {isDemoAccount(account) && <DemoArtistTag />}
                {isReadOnlyAccount(account) && <ReadOnlyArtistTag />}
                {isActive && <FontAwesomeIcon icon={faCheck} className={styles.check} />}
              </button>
            </li>
          );
        })}
        <li className={styles.addItem}>
          <button type="button" className={styles.row} onClick={switcher.openAdd} disabled={atLimit || isDisabled}>
            <span className={styles.plus} aria-hidden="true">
              <FontAwesomeIcon icon={faPlus} />
            </span>
            {intl.formatMessage(messages.add)}
          </button>
        </li>
      </ul>
      {atLimit && <ArtistSwitcherLimitBox current={current} nextPlan={nextPlan} onUpgrade={upgrade} />}
    </>
  );
};

export const ArtistSwitcherSheetList = TypedMemo(ArtistSwitcherSheetListComponent);
